import React, { useCallback, useEffect, useRef, useState } from 'react'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'

import { apiCourierLive, apiEventorLoginUrl, apiGetBookmarklet } from './Api'
import { useApiData } from './useApiData'

/**
 * Eko-hämtaren: how Eko reads Eventor now that Eventor is behind Cloudflare.
 *
 * Nothing on the server can get past that challenge — a person's ordinary browser can, by ordinary
 * browsing. So the fetching happens there: Eko starts a job, the bookmarklet asks what is waiting,
 * fetches it with the user's own Eventor session and posts it back. See web/courier.py.
 *
 * The user's part is one bookmark, once per computer, and one click per session.
 *
 * The band covers the install and the two things nobody can guess: that the bookmark is clicked in
 * the *other* tab, and that the answer nevertheless arrives back in this one. It says nothing about
 * why any of this is necessary — that is our problem, not the reader's, and an earlier version that
 * explained it was long enough that people stopped reading before the part they needed.
 */

// One tab for all of it. Named rather than '_blank' so a page reload can find the one already open.
const COURIER_TAB = 'eko-hamtaren'

// The tab we opened, kept for the life of this page. Module scope rather than a ref because
// useCourier is called from several views and they must all mean the same tab.
//
// It exists to avoid calling window.open at all on later actions: opening a *named* window that
// already exists brings it forward, so even without focus() the user was thrown to Eventor every
// time. Holding the reference means later actions touch nothing.
let courierTab: Window | null = null

// Whether Eko can open Eventor signed in for this person — i.e. whether an administrator has
// connected them to an Eventor person. Kept beside `eventorUrl` because it is answered by the same
// request and read by the same code.
let canSignIn = false

// Whether a bookmarklet is polling right now, as of the last time we asked. Kept current in the
// background because the question has to be answered inside the click — a click that waits for a
// fetch before calling window.open has lost its user gesture and the tab is blocked as a popup.
//
// Seeded from the last answer this browser got, because the moment a page has nothing to go on is
// exactly the moment it is most likely to be wrong: a reload, or a newly opened Eko tab, starts
// knowing nothing while the courier carries on polling in a tab of its own. Without the seed the
// first click after a reload races the fetch that would have told it to leave the tab alone.
const LIVE_KEY = 'eko-courier-live'

function rememberedLive(): boolean {
    // Private browsing and blocked site data both throw rather than return null.
    try {
        return window.localStorage.getItem(LIVE_KEY) === 'yes'
    } catch {
        return false
    }
}

let courierIsLive = rememberedLive()
// Where to send a tab that needs opening. It used to arrive with the job the page created; there is
// no job now, so it is cached from /courier/live — which the hook refreshes on mount, so it is
// known well before anyone clicks anything.
let eventorUrl: string | null = null

function setCourierLive(live: boolean): void {
    courierIsLive = live
    try {
        window.localStorage.setItem(LIVE_KEY, live ? 'yes' : 'no')
    } catch {
        // A page that cannot remember still works; it just starts each load uninformed.
    }
}

export async function refreshCourierLive(): Promise<void> {
    try {
        const { live, eventor_url, can_sign_in } = await apiCourierLive()
        eventorUrl = eventor_url
        canSignIn = can_sign_in
        setCourierLive(live)
    } catch {
        // No answer at all — the session may be gone. Believe it for this page, but do not write
        // that down: a failed request says nothing about whether the courier is running.
        courierIsLive = false
    }
}

/**
 * The Eventor tab to use for this action, if one needs opening at all.
 *
 * Three cases, and the whole point is to tell them apart without guessing:
 *  - we opened one and it is still there — use it, and above all do not call window.open, which
 *    brings a named window to the front and yanked the user to Eventor on every single action;
 *  - we have no handle but a courier is polling — the tab exists, in this browser, beyond our
 *    reach: after an Eko reload, or in a second Eko tab where the named-window lookup does not
 *    even find it. Opening anything here is wrong twice over, since it both steals focus and can
 *    leave a duplicate Eventor tab beside the working one;
 *  - nobody is listening — open one, which is the case the tab exists for.
 */
function tabForCourier(): { tab: Window | null, isNew: boolean } {
    // `closed` is readable across origins, unlike anything else about a tab showing Eventor.
    if (courierTab !== null && !courierTab.closed) {
        return { tab: courierTab, isNew: false }
    }
    if (courierIsLive) {
        return { tab: null, isNew: false }
    }
    return { tab: openCourierTab(), isNew: true }
}

function openCourierTab(): Window | null {
    // Named, so that an Eko reload finds the tab it opened before instead of opening a second one.
    // `noopener` is deliberately absent: it would make window.open return null and there would be
    // nothing to navigate.
    courierTab = window.open('', COURIER_TAB)
    return courierTab
}

/**
 * Send a freshly opened tab to Eventor — signed in, when Eko can arrange it.
 *
 * The link is minted here, at the moment it is used, because it is single-use and lives five
 * minutes: one per opened tab, never cached, never held. When there is none — nobody has connected
 * this user to an Eventor person, or Eventor would not mint one — the tab goes to the ordinary
 * Eventor page and the user signs in as they always have. That fallback is the normal path for
 * anyone unconnected, so it is silent rather than an error.
 */
async function sendToEventor(tab: Window): Promise<void> {
    if (eventorUrl === null) return
    let href = eventorUrl
    try {
        const minted = await apiEventorLoginUrl()
        if (minted.url !== null) href = minted.url
    } catch {
        // Eko unreachable, or Eventor's API unhappy. Neither is a reason to leave the tab blank.
    }
    tab.location.href = href
    tab.focus()
}


/**
 * Open (or re-open) the Eventor tab, signed in, because somebody asked for it.
 *
 * The automatic path above only ever navigates a *blank* tab, since navigating a working one kills
 * the script sitting in it. This is the case where that is the point: Eventor has logged the tab
 * out, and getting a live session back is worth the click on the bookmark that follows.
 */
export function openSignedInEventorTab(): void {
    // Synchronously, inside the click: a window opened after an await is a blocked popup.
    const tab = openCourierTab()
    if (tab !== null) void sendToEventor(tab)
}


function isBlank(tab: Window): boolean {
    // A just-opened tab is about:blank and readable; one the name resolved to an existing Eventor
    // tab throws on the same read, because it is another origin. The throw is the signal — it is
    // what keeps us from navigating a tab whose script is sitting there waiting to serve this job.
    try {
        return tab.location.href === 'about:blank'
    } catch {
        return false
    }
}

/**
 * The install band.
 *
 * Dragging the link to the favourites bar is the whole instruction — in Edge too, as long as the
 * bar is showing. An earlier version concluded Edge could not drag a `javascript:` link at all and
 * grew a copy-the-address fallback around that; the bar had simply been hidden. The fallback is
 * gone with the conclusion that produced it.
 *
 * Showing the bar is offered as a remedy for its being hidden, not as a step: Ctrl+Shift+B *toggles*
 * it, so telling everyone to press it hides it for whoever already had it. The shortcut is named for
 * Chrome and Edge only, because it is not the same everywhere and a confident wrong shortcut is
 * worse than none.
 *
 * What the second line promises is deliberately hedged. The script does survive between jobs — it
 * polls for the next one — but not a reload, not navigating away, and not a browser that sleeps
 * background tabs, which Edge does by default. Promising "once per login" would be right most of
 * the time and quietly wrong the rest, so it says "brukar räcka" and leans on CourierWaiting, which
 * asks for a click exactly when one is needed.
 *
 * A page cannot see whether the bookmark exists, so this is always shown rather than conditional.
 */
export function CourierBand() {
    const { data } = useApiData(apiGetBookmarklet, [])
    const [stale, setStale] = useState(false)
    const [live, setLive] = useState<boolean | null>(null)
    const [signInOffered, setSignInOffered] = useState(canSignIn)
    const linkRef = useRef<HTMLAnchorElement | null>(null)

    // Only a running script can be found to be out of date, so this is asked here rather than
    // being part of the band's own data: a bookmark nobody has clicked reports nothing, and the
    // band is already telling them to drag it.
    //
    // `live` is asked at the same time, and is per company: a courier running for one company is
    // not running for another, since the key belongs to a person *in a company*. Said here rather
    // than left for the job to go unclaimed, which is two and a half seconds and an action later.
    //
    // Asked again whenever this tab comes back to the front. Both complaints are fixed in the
    // *other* tab — by clicking the bookmark there — so the answer shown here is out of date from
    // the moment the user leaves, and it was only ever refreshed by a reload or an action.
    useEffect(() => {
        let asking = false
        const ask = () => {
            if (asking) return
            asking = true
            void apiCourierLive().then(({ stale, live, can_sign_in }) => {
                setStale(stale)
                setLive(live)
                setSignInOffered(can_sign_in)
            }).catch(() => {}).finally(() => { asking = false })
        }
        ask()
        const onVisible = () => { if (document.visibilityState === 'visible') ask() }
        document.addEventListener('visibilitychange', onVisible)
        window.addEventListener('focus', ask)
        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            window.removeEventListener('focus', ask)
        }
    }, [])

    // React 19 refuses to render a `javascript:` href: it silently substitutes a stub that throws
    // "React has blocked a javascript: URL as a security precaution", which is what a user ends up
    // with in their bookmarks. The precaution is aimed at hrefs built from untrusted input; this one
    // is our own script, served by our own server, on our own page. Setting the attribute on the DOM
    // node directly is the escape hatch — React never knows about it, so it never rewrites it.
    useEffect(() => {
        if (linkRef.current !== null && data !== null) {
            linkRef.current.setAttribute('href', data.href)
        }
    }, [data])

    if (data === null) {
        return null
    }

    return (
        <Alert severity={stale ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {live === false && (
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Ingen Eko-hämtare lyssnar. Klicka på Eko-hämtaren i Eventor-fliken. (Har du
                    bytt företag i Eko behöver du klicka igen.)
                </Typography>
            )}
            {stale && (
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Eko-hämtaren i Eventor-fliken är för gammal. Dra ut den till bokmärkesfältet
                    igen, ta bort det gamla bokmärket och klicka på det nya i Eventor-fliken. Det
                    är klicket som byter ut den som körs.
                </Typography>
            )}
            <Typography variant="body2">
                Dra{' '}
                <Link
                    ref={linkRef}
                    component="a"
                    draggable
                    onClick={event => {
                        // Clicking it here would run the script on Eko's own page, where it can do
                        // nothing — and that silence looks exactly like a broken feature.
                        event.preventDefault()
                        window.alert('Dra Eko-hämtaren till bokmärkesfältet i stället för att '
                            + 'klicka på den. Den ska klickas när du står på en Eventor-sida.')
                    }}
                    sx={{ fontWeight: 'bold', cursor: 'grab' }}
                >Eko-hämtaren</Link>
                {' '}till bokmärkesfältet — en gång per dator.
                (Syns inget bokmärkesfält? <strong>Ctrl+Shift+B</strong>.)
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
                När du hämtar något från Eventor öppnar Eko en Eventor-flik — färdiginloggad, om du
                är kopplad till en person i Eventor. Klicka på <strong>Eko-hämtaren</strong> där och
                gå sedan tillbaka hit. Resultatet visas i den här fliken.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
                Låt Eventor-fliken vara öppen, så slipper du klicka nästa gång.
            </Typography>
            {/* For the one case the automatic path leaves alone: a tab that is open but has been
                logged out. Navigating it is what fixes that, and what the automatic path must
                never do.

                Shown only to someone Eko can actually sign in. For anyone else the same click
                would open the plain Eventor page, and a button that says "inloggad" and delivers a
                login form is worse than no button. */}
            {signInOffered &&
                <Button size="small" variant="outlined" sx={{ mt: 1 }}
                        onClick={openSignedInEventorTab}>
                    Öppna Eventor-fliken inloggad
                </Button>}
        </Alert>
    )
}

export type CourierRun = {
    /** Make sure a courier is listening — opening the Eventor tab if need be — and run `work`.
     *  Nothing is passed to it: the work fetches through the signed-in person's mailbox, which the
     *  server resolves from the session. There is no description either — it used to label the job
     *  in the tab's overlay, and the overlay now counts pages instead. */
    run: <T>(work: () => Promise<T>) => Promise<T>
    /** True only when no courier is listening, so the user really does need to go and click. */
    needsClick: boolean
}

export function useCourier(): CourierRun {
    const [needsClick, setNeedsClick] = useState(false)

    // Asked once per view, and again after every action, so the answer at the moment of the click
    // is at most one action old.
    useEffect(() => {
        void refreshCourierLive()
    }, [])

    const run = useCallback(async <T,>(work: () => Promise<T>) => {
        // Before the first await, so it still counts as the click the user just made: opened after
        // a request — the obvious order — it is a popup as far as the browser is concerned, and is
        // silently blocked. `noopener` is deliberately absent, since it would make window.open
        // return null and there would be nothing to navigate.
        const { tab, isNew } = tabForCourier()
        try {
            if (tab !== null && isNew && isBlank(tab)) {
                // Only a brand-new tab is sent anywhere. An existing one needs nothing from us, and
                // navigating it would *reload* it, killing the script sitting there ready to fetch.
                //
                // It is also the one case where taking the user there is the point: they have to
                // click the bookmark, and sign in first if Eko could not sign them in. Later
                // actions leave them where they are. Not awaited: the job below should start now,
                // not after a round trip for a link.
                void sendToEventor(tab)
            }
            // A couple of seconds in, ask whether a courier is listening at all. Nothing listening
            // means the bookmark has not been clicked, or the tab is gone, or the company was
            // switched — and *then* it is worth asking for attention. Slow is not unattended, and
            // guessing from elapsed time both nagged during healthy imports and stole focus to a
            // tab that needed nothing.
            const nudge = window.setTimeout(() => {
                void apiCourierLive().then(({ live }) => {
                    if (live) {
                        return
                    }
                    setCourierLive(false)
                    setNeedsClick(true)
                    // Still inside the click's transient activation, which browsers keep alive for
                    // about five seconds — so a tab can still be opened for the case this exists to
                    // catch: we trusted a courier that has since gone away.
                    const target = tab ?? openCourierTab()
                    if (target !== null && isBlank(target)) {
                        void sendToEventor(target)
                    } else {
                        target?.focus()
                    }
                }).catch(() => {})
            }, 2500)
            try {
                // No token to hand over, and nothing to finish afterwards. The work simply fetches
                // through this person's mailbox, which exists for as long as they do.
                return await work()
            } finally {
                window.clearTimeout(nudge)
                setNeedsClick(false)
                void refreshCourierLive()
            }
        } finally {
            setNeedsClick(false)
        }
    }, [])

    return { run, needsClick }
}

/**
 * "Click the bookmark" — shown only when nobody has picked the job up.
 *
 * Once the script is running it takes later jobs by itself, so nearly every action needs no click.
 * An earlier version showed this whenever an action took more than a couple of seconds, which is
 * also what a large import looks like — so it nagged hardest exactly when everything was working.
 * The signal is now whether the job was *claimed*, which the server knows for certain.
 */
export function CourierWaiting({ waiting }: { waiting: boolean }) {
    if (!waiting) {
        return null
    }
    return (
        <Box sx={{ mb: 2 }}>
            <Alert severity="warning">
                Väntar på Eko-hämtaren. Klicka på den i Eventor-fliken.
            </Alert>
        </Box>
    )
}
